import http from "@/api/request";
import type { OrganizationDetailVO } from "@/api/system/organizations/type";
import { EMPLOYEE_OPS_BASE } from "@/config/apiBaseUrlConfig";

import type {
  EmployeeDetailVO,
  EmployeeOnboardRequest,
  EmployeeOnboardResultVO,
  EmployeePageQueryRequest,
  EmployeeStatusUpdateRequest,
  GetOrgRoleAssignOptionsParams,
  PageDataEmployeePageItemVO,
  ShopRoleAssignOptionsVO,
  ShopRoleConfigSaveRequest,
} from "./type";

/**
 * OPS 员工分页列表
 * POST /service-chijing-employee/api/ops/employee/page
 * @param data 分页与筛选条件
 */
export const getEmployeePage = (data: EmployeePageQueryRequest) => {
  return http.post<PageDataEmployeePageItemVO>(`${EMPLOYEE_OPS_BASE}/page`, data);
};

/**
 * OPS 员工详情
 * GET /service-chijing-employee/api/ops/employee/{id}
 * @param id 员工 ID
 */
export const getEmployee = (id: string) => {
  return http.get<EmployeeDetailVO>(`${EMPLOYEE_OPS_BASE}/${id}`);
};

/**
 * 查询员工绑定组织
 * GET /service-chijing-employee/api/ops/employee/getOrganizations/{userId}
 * 根据 userId 查询员工 shopId，再远程批量查询 Organization 组织信息（按 orgId 去重）
 * @param userId 用户 ID
 */
export const getEmployeeOrganizations = (userId: string) => {
  return http.get<OrganizationDetailVO[]>(`${EMPLOYEE_OPS_BASE}/getOrganizations/${userId}`);
};

/**
 * onboard Step4：确认邀请员工
 * POST /service-chijing-employee/api/ops/employee/onboard
 * @param data 邀请员工参数
 */
export const onboardEmployee = (data: EmployeeOnboardRequest) => {
  return http.post<EmployeeOnboardResultVO>(`${EMPLOYEE_OPS_BASE}/onboard`, data);
};

/**
 * 启用/停用员工（仅更新档案 employee_status，不改 OPS 角色绑定；已离职不可操作）
 * PUT /service-chijing-employee/api/ops/employee/{id}/status
 * @param id 员工 ID 或工号
 * @param data 启停参数
 */
export const updateEmployeeStatus = (id: string, data: EmployeeStatusUpdateRequest) => {
  return http.put<EmployeeDetailVO>(`${EMPLOYEE_OPS_BASE}/${id}/status`, data);
};

/**
 * 穿梭框选项：左=可分配，右=已绑定
 * GET /service-chijing-employee/api/ops/employee/{employeeId}/org-role-assign-options
 * 需权限 ops:employee:role:assign
 * @param employeeId 员工档案主键
 * @param params.orgId 组织 ID，默认取员工 shopId
 */
export const getOrgRoleAssignOptions = (
  employeeId: string,
  params?: GetOrgRoleAssignOptionsParams,
) => {
  return http.get<ShopRoleAssignOptionsVO>(
    `${EMPLOYEE_OPS_BASE}/${employeeId}/org-role-assign-options`,
    { params },
  );
};

/**
 * 保存角色配置（穿梭框全量覆盖）
 * PUT /service-chijing-employee/api/ops/employee/{employeeId}/org-role-config
 * 需权限 ops:employee:role:assign
 * @param employeeId 员工档案主键
 * @param data 最终角色名称列表
 * @param params.orgId 组织 ID，默认取员工 shopId
 */
export const saveOrgRoleConfig = (
  employeeId: string,
  data: ShopRoleConfigSaveRequest,
  params?: GetOrgRoleAssignOptionsParams,
) => {
  return http.put<void>(`${EMPLOYEE_OPS_BASE}/${employeeId}/org-role-config`, data, {
    params,
  });
};
